import React, { useEffect, useState } from "react";
import { X, ShieldCheck, EyeOff } from "lucide-react";

/**
 * MapLightbox — Full-size watermarked preview of a Bush Map.
 *
 * Protections (no blur, no lens):
 *  - Repeating diagonal "© BUSH MAPS · SAMPLE" watermark grid overlaid on top
 *  - Right-click / drag / selection disabled
 *  - Visibility-change & screenshot-shortcut → blackout
 *  - No CSS background-image; only <img> with watermark overlay above
 */
export default function MapLightbox({ open, onClose, map }) {
  const [blackout, setBlackout] = useState(false);

  // Blackout deterrents — visibility change & common screenshot shortcuts
  useEffect(() => {
    if (!open) return undefined;
    const onVis = () => setBlackout(document.hidden);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (
        e.key === "PrintScreen" ||
        (e.metaKey && e.shiftKey) ||
        (e.ctrlKey && (e.key === "p" || e.key === "P" || e.key === "s" || e.key === "S"))
      ) {
        setBlackout(true);
        setTimeout(() => setBlackout(false), 1200);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open || !map) return null;

  return (
    <div
      data-testid="map-lightbox"
      className="fixed inset-0 z-[100] flex flex-col bg-[#1A1612]/95 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 text-[#FDFBF7]">
        <div className="flex items-center gap-4 min-w-0">
          <div className="hidden sm:block text-[11px] tracking-[0.24em] uppercase text-[#E9C7A8]">
            {map.region}
          </div>
          <div className="font-serif-display text-xl md:text-2xl truncate">
            {map.title}
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="map-lightbox-close"
          className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/90 hover:bg-white/10"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      {/* Stage */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-10 select-none overflow-auto">
        <div
          onContextMenu={(e) => e.preventDefault()}
          className="relative w-full max-w-6xl bg-[#0F0D0A] overflow-hidden shadow-2xl ring-1 ring-white/10"
        >
          {/* The map (no blur) */}
          <img
            src={map.url}
            alt={map.title}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className="block w-full h-auto pointer-events-none"
            style={{
              userSelect: "none",
              WebkitUserDrag: "none",
            }}
          />

          {/* Repeating diagonal watermark overlay — protection */}
          <div
            className="absolute inset-0 pointer-events-none flex flex-wrap content-around justify-around"
            style={{
              transform: "rotate(-22deg) scale(1.5)",
              transformOrigin: "center",
            }}
          >
            {Array.from({ length: 40 }).map((_, i) => (
              <span
                key={i}
                className="text-xs md:text-sm tracking-[0.36em] uppercase whitespace-nowrap px-6 py-3"
                style={{
                  color: "rgba(45,38,33,0.42)",
                  textShadow: "0 1px 0 rgba(253,251,247,0.45)",
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: "italic",
                  fontWeight: 500,
                }}
              >
                Sample · © Bush Maps · Sample · © Bush Maps
              </span>
            ))}
          </div>

          {/* Subtle diagonal stripe (extra texture) */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(-22deg, rgba(45,38,33,0) 0 110px, rgba(45,38,33,0.08) 110px 112px)",
            }}
          />

          {/* Bottom-right brand stamp */}
          <div className="absolute bottom-4 right-4 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase bg-[#A95A3F]/95 text-white pointer-events-none">
            Sample · {map.title}
          </div>

          {/* Blackout overlay */}
          {blackout && (
            <div className="absolute inset-0 bg-[#0A0807] flex flex-col items-center justify-center gap-3 text-white">
              <EyeOff size={28} className="text-[#E9C7A8]" />
              <div className="font-serif-display text-2xl">Preview paused</div>
              <div className="text-xs text-white/60 max-w-xs text-center tracking-wide">
                Sample maps are visible only while this window is in focus.
                Return here to continue exploring.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-white/70 text-xs">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-[#E9C7A8]" />
          Sample preview only. Full-resolution maps are shared privately with project partners.
        </div>
        <div className="hidden md:block tracking-wide">
          Press <kbd className="px-1.5 py-0.5 border border-white/20 rounded text-[10px]">Esc</kbd> to close
        </div>
      </div>
    </div>
  );
}
